import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const JoinAsPro = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    category: '',
    serviceAreas: [],
    notes: ''
  });

  const categories = [
    { value: 'agent', label: 'Real Estate Agent', icon: 'Home' },
    { value: 'broker', label: 'Mortgage Broker', icon: 'DollarSign' },
    { value: 'lawyer', label: 'Property Lawyer', icon: 'Scale' },
    { value: 'photographer', label: 'Photographer', icon: 'Camera' },
    { value: 'stylist', label: 'Property Stylist', icon: 'Palette' }
  ];

  const australianStates = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'SA', label: 'South Australia' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'NT', label: 'Northern Territory' },
    { value: 'ACT', label: 'Australian Capital Territory' }
  ];

  const handleStep1Submit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!formData?.name || !formData?.email || !formData?.password) {
      setError('Please fill in all required fields');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password
      });

      if (authError) throw authError;

      const { error: userError } = await supabase?.from('users')?.insert([
          {
            id: authData?.user?.id,
            name: formData?.name,
            email: formData?.email,
            role: 'pro',
            phone: formData?.phone
          }
        ]);

      if (userError) throw userError;

      const { error: profileError } = await supabase?.from('pro_profiles')?.insert([
          {
            user_id: authData?.user?.id,
            category: formData?.category,
            service_areas: formData?.serviceAreas,
            notes: formData?.notes,
            active: true
          }
        ]);

      if (profileError) throw profileError;

      navigate('/dashboard');
    } catch (err) {
      console.error('Error creating pro account:', err);
      setError(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleServiceArea = (state) => {
    if (formData?.serviceAreas?.includes(state)) {
      setFormData({
        ...formData,
        serviceAreas: formData?.serviceAreas?.filter(s => s !== state)
      });
    } else {
      setFormData({
        ...formData,
        serviceAreas: [...formData?.serviceAreas, state]
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Join as a Pro - ListingLens</title>
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-4">
              <Icon name="Briefcase" size={32} color="white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Join as a Professional</h1>
            <p className="text-muted-foreground">Connect with property buyers and sellers</p>
          </div>

          <div className="bg-card rounded-xl border border-border p-6 md:p-8">
            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {step > 1 ? <Icon name="Check" size={20} /> : '1'}
                </div>
                <div className={`w-16 h-1 rounded-full ${
                  step >= 2 ? 'bg-primary' : 'bg-muted'
                }`} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  2
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3 mb-6 flex items-start gap-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {step === 1 ? (
              <form onSubmit={handleStep1Submit} className="space-y-4">
                <h2 className="text-xl font-bold text-foreground mb-4">Account Information</h2>
                
                <Input
                  label="Full Name"
                  type="text"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  required
                  disabled={loading}
                />

                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                  disabled={loading}
                />

                <Input
                  label="Password"
                  type="password"
                  value={formData?.password}
                  onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                  required
                  disabled={loading}
                />

                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  disabled={loading}
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => navigate('/authentication')}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="default" fullWidth iconName="ArrowRight" iconPosition="right">
                    Continue
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleStep2Submit} className="space-y-6">
                <h2 className="text-xl font-bold text-foreground mb-4">Professional Profile</h2>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Your Category</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categories?.map((cat) => (
                      <button
                        key={cat?.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat?.value })}
                        className={`p-4 rounded-lg border-2 transition-all text-left ${
                          formData?.category === cat?.value
                            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                        }`}
                      >
                        <Icon name={cat?.icon} size={24} color="var(--color-primary)" className="mb-2" />
                        <p className="font-semibold text-foreground">{cat?.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Service Areas</label>
                  <div className="grid grid-cols-2 gap-2">
                    {australianStates?.map((state) => (
                      <div
                        key={state?.value}
                        onClick={() => toggleServiceArea(state?.value)}
                        className="flex items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 cursor-pointer transition-all"
                      >
                        <Checkbox
                          checked={formData?.serviceAreas?.includes(state?.value)}
                          onChange={() => toggleServiceArea(state?.value)}
                        />
                        <span className="text-sm text-foreground">{state?.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">About Your Services (Optional)</label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                    rows={4}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Tell us about your experience and services..."
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => setStep(1)}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="success"
                    fullWidth
                    loading={loading}
                    iconName="Check"
                  >
                    Create Pro Account
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default JoinAsPro;